'use strict';


import { Effect } from './Effect';
import { FullScreenQuad, Pass } from 'three/examples/jsm/postprocessing/Pass';
import { Color, ShaderMaterial, UniformsUtils, WebGLRenderTarget } from 'three';
import { CopyShader } from 'three/examples/jsm/shaders/CopyShader';
import vertex from './Shaders/MixerPass/vertex.glsl';
import fragment from './Shaders/MixerPass/fragment.glsl';

export class MixerEffect extends Effect{
	constructor (camera,width,height) {
		super();
		
		this.effect = new MixerPass(camera,width,height);
		this.active = false;
	}
	
	set active (state) {
		this.takeScreenshot();
		this.mix(0);
		this.setTransitionScale(1);
		super.active = state;
	}
	
	mix(value) {
		this.effect.mix(value);
	}
	
	setTransitionScale(value) {
		this.effect.setTransitionScale(value);
	}
	
	takeScreenshot() {
		this.effect.takeScreenshot();
	}
	
	fromRenderTarget(rt,renderer) {
		this.effect.fromRenderTarget(rt,renderer);
	}
}


class MixerPass extends Pass{
	constructor (camera,width,height) {
		super();
	
		this.camera = camera;
		this.width = width;
		this.height = height;
		
		this.defaultMaterial = new ShaderMaterial({
			uniforms: UniformsUtils.clone( CopyShader.uniforms ),
			vertexShader: CopyShader.vertexShader,
			fragmentShader: CopyShader.fragmentShader,
			transparent: true,
			depthTest: false,
			depthWrite: false,
		});
		
		this.mixerMaterial = new ShaderMaterial({
			uniforms: {
				tDiffuse: {value: null},
				tScreenshot: {value: null},
				uMixer: {value: 0},
				useTransition: {value: true},
				uTransitionScale: {value: 1}
			},
			vertexShader: vertex,
			fragmentShader: fragment,
			transparent: true,
			depthWrite: false,
			depthTest: false
		});
		
		this.defaultRt = new WebGLRenderTarget(this.width,this.height);
		this.screenshotRt = new WebGLRenderTarget(this.width,this.height);
		
		this.fsQuad = new FullScreenQuad(null);
		this.originalClearColor = new Color();
		this.needsSwap = false;
		this.screenshot = false;
	}
	
	setSize( width, height) {
		this.width = width;
		this.height = height;
		this.defaultRt.setSize(width,height);
	}
	
	mix(value) {
		this.mixerMaterial.uniforms.uMixer.value = value;
	}
	
	setTransitionScale(value) {
		this.mixerMaterial.uniforms.uTransitionScale.value = value;
	}
	
	takeScreenshot() {
		this.screenshot = false;
	}
	
	fromRenderTarget(rt,renderer) {
		this.screenshot = true;
		this.defaultMaterial.uniforms.tDiffuse.value = rt.texture;
		this.renderPass(renderer,this.defaultMaterial,this.screenshotRt);
	}
	
	render( renderer, writeBuffer, readBuffer) {
		this.defaultMaterial.uniforms.tDiffuse.value = readBuffer.texture;
		this.renderPass(renderer,this.defaultMaterial,this.defaultRt);
		
		if (!this.screenshot) {
			this.renderPass(renderer,this.defaultMaterial,this.screenshotRt);
			this.screenshot = true;
		}
		
		this.mixerMaterial.uniforms.tDiffuse.value = this.defaultRt.texture;
		this.mixerMaterial.uniforms.tScreenshot.value = this.screenshotRt.texture;
		this.renderPass(renderer,this.mixerMaterial,this.renderToScreen ? null : readBuffer);
	}
	
	renderPass( renderer, passMaterial, renderTarget, clearColor, clearAlpha ) {
		// save original state
		renderer.getClearColor( this.originalClearColor );
		const originalClearAlpha = renderer.getClearAlpha();
		const originalAutoClear = renderer.autoClear;
		
		renderer.setRenderTarget( renderTarget );
		
		// setup pass state
		renderer.autoClear = false;
		if ( ( clearColor !== undefined ) && ( clearColor !== null ) ) {
			
			renderer.setClearColor( clearColor );
			renderer.setClearAlpha( clearAlpha || 0.0 );
			renderer.clear();
			
		}
		
		this.fsQuad.material = passMaterial;
		this.fsQuad.render( renderer );
		
		// restore original state
		renderer.autoClear = originalAutoClear;
		renderer.setClearColor( this.originalClearColor );
		renderer.setClearAlpha( originalClearAlpha );
	}
	
}
